'use client';
import { useEffect } from 'react';

import { useApi } from '@/hooks/useApi';

import { BlockComponentModel } from '@/components/cms/types/blok-component';
import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';

import Heading from '@/decodo/components/atoms/Heading';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/decodo/components/atoms/Image/ImageStrapi';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import { Text } from '@/decodo/components/atoms/Text';
import { BlockHeaderContent } from '@/features/utils/BlockHeaderContentDC';
import { BlockWrapper } from '@/features/utils/BlockWrapperDC';

import IconDevice from '~/svg/IconDevice.svg';
import IconMapMarker from '~/svg/IconMapMarker.svg';
import IconPhoneCall from '~/svg/IconPhoneCall.svg';
import IconUniversal from '~/svg/IconUniversal.svg';

export type WhatIsMyIpHeroProps = {
  buttons?: LinkButtonStrapiProps[];
  image: ImageStrapiProps;
};

type IpData = {
  browser: { name: string; version: string };
  platform: { os: string; name?: string };
  engine: unknown;
  isp: {
    isp: string;
    asn: number;
    domain: string;
    organization: string;
  };
  city: {
    name: string;
    code: string;
    state: string;
    time_zone: string;
    zip_code: string;
    latitude: number;
    longitude: number;
  };
  proxy: { ip: string };
  country: { code: string; name: string; continent: string };
};

export function WhatIsMyIpHeroStrapi({
  block,
  ...rest
}: BlockComponentModel<WhatIsMyIpHeroProps>) {
  const { buttons, image } = block;
  const { data, makeApiCall } = useApi<IpData>({
    url: 'https://ip.decodo.com/json',
    method: 'GET',
  });

  useEffect(() => {
    makeApiCall();
  }, []);

  return (
    <>
      <BlockWrapper
        {...rest}
        contentFit='mid'
        data-tracker-section={SECTION_NAMES.WHAT_IS_MY_IP}
      >
        <div>
          <BlockHeaderContent alignment='left'>
            <div className='flex w-full flex-row justify-between gap-16'>
              <div className='flex w-full flex-[2] flex-col justify-center gap-6'>
                <Heading level='h1'>
                  Your Public IP Address: {data?.proxy.ip}
                </Heading>
                <Text>Location: {data?.city.name}</Text>
                <Text>Country: {data?.country.code}</Text>
                <div className='flex gap-6 pt-2'>
                  {buttons?.map((button, index) => (
                    <LinkButtonStrapi
                      key={index}
                      block={button}
                      inner={{ classNames: { root: 'w-full md:w-auto' } }}
                    />
                  ))}
                </div>
              </div>
              <div className='hidden flex-1 md:block'>
                <ImageStrapi
                  block={image}
                  inner={{
                    classNames: {
                      image: 'md:max-w-[360px] md:max-h-[360px]',
                    },
                  }}
                />
              </div>
            </div>
          </BlockHeaderContent>
        </div>
      </BlockWrapper>
      <BlockWrapper
        data-tracker-section={SECTION_NAMES.WHAT_IS_MY_IP}
        {...rest}
      >
        <div>
          <BlockHeaderContent alignment='left'>
            <div className='flex w-full flex-wrap justify-between gap-6'>
              <div className='flex w-[200px]  flex-col gap-3'>
                <div className='flex flex-col gap-4'>
                  <IconMapMarker className='text-brand-500 h-9 w-9' />
                  <Text variant='subtitle-l-d'>Location</Text>
                </div>
                <Text>City: {data?.city.name}</Text>
                <Text>Code: {data?.city.code}</Text>
                <Text>State: {data?.city.state}</Text>
                <Text>Time zone: {data?.city.time_zone}</Text>
              </div>
              <div className='flex w-[200px]  flex-col gap-3'>
                <div className='flex flex-col gap-4'>
                  <IconPhoneCall className='text-brand-500 h-9 w-9' />
                  <Text variant='subtitle-l-d'>ISP</Text>
                </div>
                <Text>Provider: {data?.isp.isp}</Text>
                <Text>ASN: {data?.isp.asn}</Text>
                <Text>Organization: {data?.isp.organization}</Text>
              </div>
              <div className='flex w-[200px]  flex-col gap-3'>
                <div className='flex flex-col gap-4'>
                  <IconDevice className='text-brand-500 h-9 w-9' />
                  <Text variant='subtitle-l-d'>Platform</Text>
                </div>
                <Text>Name: {data?.platform?.name}</Text>
                <Text>OS: {data?.platform?.os}</Text>
              </div>
              <div className='flex w-[200px]  flex-col gap-3'>
                <div className='flex flex-col gap-4'>
                  <IconUniversal className='text-brand-500 h-9 w-9' />
                  <Text variant='subtitle-l-d'>Browser</Text>
                </div>
                <Text>Name: {data?.browser.name}</Text>
                <Text>Version: {data?.browser.version}</Text>
              </div>
            </div>
          </BlockHeaderContent>
        </div>
      </BlockWrapper>
    </>
  );
}
